import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import Calendar from "@toast-ui/react-calendar"
import "@toast-ui/calendar/dist/toastui-calendar.min.css"
import BasePage from "../../../components/layout/BasePage"
import "../../../style/calendar.css"
import BaseHeadBanner from "../../../components/elements/BaseHeadBanner"
import BaseButton from "../../../components/elements/BaseButton"
import { ReactComponent as PLUSCIRCLE } from "../../../assets/PlusInCircleIcon.svg"
import { ReactComponent as ARROWRIGHT } from "../../../assets/ChevronRight.svg"
import { ReactComponent as ARROWLEFT } from "../../../assets/ChevronLeft.svg"
import { ReactComponent as CALENDAR } from "../../../assets/CalendarICON.svg"
import { ReactComponent as ARROWRIGHTLEFT } from "../../../assets/ArrowsRightLeft.svg"
import BaseButtonRow from "../../../components/elements/BaseButtonRow"
import BaseInputSelection from "../../../components/elements/BaseInputSelection"
import { getPaginatedData } from "../../api/apiCalls"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import {
    loadWithExpiryFromLocalStorage,
    rearrangeAbsencesDataForCalendar,
    rearrangeSessionDataForCalendar,
    rearrangeSessionDataForDB,
    rearrangeSessionDataForDBUpdate,
    useDelayWhenSearchWithPagination,
} from "../../../services/functions"
import { useNavigate, useParams } from "react-router-dom"
import { getTasksByFilter } from "../api/apiCalls"
import {
    useAddSessionMutation,
    useAssignTaskToSessionMutation,
    useCheckInOrOutToSessionMutation,
    useDeleteSessionMutation,
    useUnAssignTaskToSessionMutation,
    useUpdateSessionAttendeesMutation,
    useUpdateSessionMutation,
} from "../api/services"
import { getSingleTaskType, OptimizeRouteResultType, sessionCalendarDataType, sessionCalendarTransferDataType, sessionGetDataType } from "../types"
import dayjs, { Dayjs } from "dayjs"
import ErrorPage from "../../404/ErrorPage"
import SessionDetailSidebar from "../components/SessionDetailSidebar"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import { getEmployeeByIdData, getEmployeeNamesOnly } from "../../Mitarbeiter/api/apiCalls"
import EditAttendeesOfSessionModal from "../components/EditAttendeesOfSessionModal"
import { ModalContext } from "../../../components/contexts/ModalContext"
import isoWeek from "dayjs/plugin/isoWeek"
import Loading from "../../../components/common/Loading"
import { GeneralQueryKeys, Navigation } from "../../../api/constants"
import { useForm } from "antd/es/form/Form"
import { getAbsencesByEmployeeId } from "../../Abwesenheiten/api/apiCalls"
import RouteOptimizationModal from "../components/RouteOptimizationModal/RouteOptimizationModal"
import { getObjectAddressOnly } from "../../Objekte/api/apiCalls"
import ModalCheckAction from "../../../components/elements/ModalCheckAction"
import { P } from "../../../auth/permissions"
import { Authorized } from "../../../components/authentication/Authorized"
import { useAbility } from "../../../components/contexts/AbilityContext"

type ViewType = "week" | "day"

export default function CalendarPage() {
    // General settings
    const navigate = useNavigate()
    const modalCon = useContext(ModalContext)
    const ability = useAbility()
    const calendarRef = useRef<any>()
    const { id } = useParams()
    const [nameOfCurrentEmployee, setNameOfCurrentEmployee] = useState<string>()

    // GET employee by id
    const queryKeySingleEmployee = GeneralQueryKeys.EMPLOYEE_SINGLE + (id ?? "")
    const {
        isLoading: employeeisLoading,
        error: empployeeError,
        data: singleEmployeeData,
    } = useQuery({
        queryKey: [queryKeySingleEmployee],
        queryFn: () =>
            getEmployeeByIdData(
                id ?? "",
                "-kcId,-email,-geb,-position,-archived,-contractStart,-contractEnd,-trailperiod,-vehicle,-notes,-s3Documents,-activeSession,-vacationContingentPerYear,-restVacationDays,-driversLicence,-__v,-address"
            ),
        enabled: !!id,
    })

    // GET Employees session
    dayjs.extend(isoWeek)
    const [currentCalendarView, setcurrentCalendarView] = useState<ViewType>("week")
    const [startInterval, setstartInterval] = useState(currentCalendarView === "day" ? dayjs().hour(0).minute(0).second(0).millisecond(0) : dayjs().startOf("isoWeek").toDate())
    const queryKeySingleEmployeeSession = GeneralQueryKeys.EMPLOYEE_SESSION + (id ?? "") + startInterval
    const {
        isLoading: eventisLoading,
        error: eventError,
        data: eventData,
    } = useQuery({
        queryKey: [queryKeySingleEmployeeSession],
        queryFn: () =>
            getPaginatedData({
                filter: true,
                page: 1,
                pageType: "session",
                limit: 1000,
                startDate: startInterval.toISOString(),
                endDate: dayjs(startInterval)
                    .add(currentCalendarView === "day" ? 1 : 6, "days")
                    .toDate()
                    .toISOString(),
                employeeId: id,
            }),
        enabled: !!(id && startInterval && currentCalendarView),
    })
    const queriedEventData = useMemo(() => eventData, [eventData])

    // GET absences by employee id
    const queryKeyAbsencesByEmployeeId = GeneralQueryKeys.ABSENCE + (id ?? "") + startInterval + currentCalendarView
    const {
        isLoading: absencesIsLoading,
        error: absencesError,
        data: absencesData,
    } = useQuery({
        queryKey: [queryKeyAbsencesByEmployeeId],
        queryFn: () =>
            getAbsencesByEmployeeId(
                id ?? "",
                dayjs(startInterval).format("YYYY-MM-DD"),
                dayjs(startInterval)
                    .add(currentCalendarView === "day" ? 1 : 6, "days")
                    .format("YYYY-MM-DD")
            ),
        enabled: !!id,
    })

    const [allFetchedEvents, setallFetchedEvents] = useState<(sessionCalendarDataType | sessionCalendarTransferDataType)[]>([])
    useEffect(() => {
        let allCurrentEvents: (sessionCalendarDataType | sessionCalendarTransferDataType)[] = []
        if (queriedEventData) allCurrentEvents = allCurrentEvents.concat(rearrangeSessionDataForCalendar(queriedEventData, id ?? ""))
        if (absencesData && singleEmployeeData)
            allCurrentEvents = allCurrentEvents.concat(rearrangeAbsencesDataForCalendar(absencesData, singleEmployeeData?.first_name + " " + singleEmployeeData?.last_name))
        setallFetchedEvents(allCurrentEvents)
    }, [eventData, absencesData, singleEmployeeData, queriedEventData, id])
    const [showDetailEvent, setshowDetailEvent] = useState<sessionCalendarDataType | "new">()

    // set calendar view to current date range
    useEffect(() => {
        if (currentCalendarView === "week") {
            if (calendarRef.current) calendarRef.current.getInstance().setDate(startInterval)
        } else if (currentCalendarView === "day") {
            if (calendarRef.current) calendarRef.current.getInstance().setDate(startInterval)
        }
    }, [currentCalendarView, startInterval])

    // Change to next week or day
    const goToNextWeek = () => {
        if (currentCalendarView === "week") {
            setstartInterval(dayjs(startInterval).add(7, "days").toDate())
        } else if (currentCalendarView === "day") {
            setstartInterval(dayjs(startInterval).add(1, "day").toDate())
        }
    }

    // Change to previous week or day
    const goToPrevWeek = () => {
        if (currentCalendarView === "week") {
            setstartInterval(dayjs(startInterval).subtract(7, "days").toDate())
        } else if (currentCalendarView === "day") {
            setstartInterval(dayjs(startInterval).subtract(1, "day").toDate())
        }
    }

    // Change calendar view
    const handleChangeCalendarView = () => {
        if (currentCalendarView === "week") {
            setcurrentCalendarView("day")
            setstartInterval(dayjs().hour(0).minute(0).second(0).millisecond(0))
        } else if (currentCalendarView === "day") {
            setcurrentCalendarView("week")
            setstartInterval(dayjs().startOf("isoWeek").toDate())
        }
    }

    // Kalender-Styling
    const calendars = [
        {
            id: "cal1",
            name: "Work",
            backgroundColor: "var(--p-hex-50)",
            borderColor: "var(--p-hex)",
        },
        {
            id: "cal1.1",
            name: "Work",
            backgroundColor: "var(--p-hex)",
            borderColor: "var(--p-hex)",
        },
        {
            id: "cal2",
            name: "Absence",
            backgroundColor: "#acb4c2",
            borderColor: "#acb4c2",
        },
        {
            id: "cal3",
            name: "Transfer",
            backgroundColor: "#acb5",
            borderColor: "#acb",
        },
    ]

    // UPDATE Sessions (time)
    const queryClient = useQueryClient()
    const [eventUpdate, seteventUpdate] = useState<any>()
    const { mutate: updateSession } = useUpdateSessionMutation(queryClient, queryKeySingleEmployeeSession)
    // function to make sure to only update real sessions - no transfers or absences
    const handleBeforeUpdateSchedule = (eventUpdate: any) => {
        if (eventUpdate.event.calendarId !== "cal2" && eventUpdate.event.calendarId !== "cal3" && eventUpdate.event.calendarId !== "cal1.1") {
            updateSessionTimeOnly(eventUpdate)
        } else if (eventUpdate.event.calendarId === "cal1.1") {
            seteventUpdate(eventUpdate)
            modalCon?.trigger(3)
        }
    }
    // function to update session time only
    const updateSessionTimeOnly = (eventUpdate: any) => {
        const { event, changes } = eventUpdate
        setallFetchedEvents((prevEvents: (sessionCalendarDataType | sessionCalendarTransferDataType)[]) =>
            prevEvents.map((prevEvent) => {
                return prevEvent?.id === event.id ? { ...prevEvent, ...changes } : prevEvent
            })
        )
        updateSession({
            id: event.id ?? "",
            body: {
                startAt: dayjs(changes.hasOwnProperty("start") ? changes.start.d : event.start.d).toDate(),
                endAt: dayjs(changes.end.d).toDate(),
            } as sessionGetDataType,
        })
        setshowDetailEvent(undefined)
        seteventUpdate(undefined)
    }

    // UPDATE session (dataset)
    const [updateSessionCopy, setupdateSessionCopy] = useState<sessionCalendarDataType>()
    const handleUpdateSession = () => {
        handleAssignOrUnassignTasks(typeof showDetailEvent !== "string" ? showDetailEvent?.id ?? "" : "")
        updateSession({
            id: typeof showDetailEvent !== "string" ? showDetailEvent?.id ?? "" : "",
            body: rearrangeSessionDataForDBUpdate(updateSessionCopy as sessionCalendarDataType, showDetailEvent as sessionCalendarDataType),
        })
        setshowDetailEvent(undefined)
    }

    // UPDATE session (attendees/employees)
    const { mutate: updateSessionAttendees } = useUpdateSessionAttendeesMutation(queryClient, queryKeySingleEmployeeSession)
    const handleUpdateAttendees = () => {
        if (typeof showDetailEvent !== "string" && showDetailEvent !== undefined) {
            updateSessionAttendees({
                id: showDetailEvent.id,
                body: {
                    attendees: showDetailEvent.attendees.map((item) => item._id),
                } as sessionGetDataType,
            })
            modalCon?.trigger(1)
        }
    }

    // Function to only assign tasks
    const handleAssignTasks = (id: string) => {
        if (typeof showDetailEvent !== "string" && showDetailEvent?.tasks !== undefined && !assignedTasks.every((item) => showDetailEvent.tasks.includes(item))) {
            assignTasks({
                id: id,
                body: assignedTasks?.filter((assignedItem) => !showDetailEvent?.tasks?.some((originalTask: getSingleTaskType) => assignedItem._id === originalTask._id)).map((item) => item._id),
            })
        }
    }

    // Function to only unassign tasks
    const handleUnassignTasks = (id: string, areBothAssigningsExecuted: boolean) => {
        if (!unassignedTasks?.every((item) => tasksByObjectData.includes(item))) {
            unassignTasks({
                id: id,
                areBothAssigningsExecuted: areBothAssigningsExecuted,
                body: unassignedTasks?.filter((unassignedItem) => !tasksByObjectData.some((originalTask: getSingleTaskType) => unassignedItem._id === originalTask._id)).map((item) => item._id) ?? [],
            })
        }
    }

    // PUT tasks (-> assign to session)
    const [assignedTasks, setassginedTasks] = useState<getSingleTaskType[]>([])
    useEffect(() => {
        if (typeof showDetailEvent !== "string") {
            setassginedTasks(showDetailEvent?.tasks ?? [])
        }
    }, [showDetailEvent])
    const { mutate: assignTasks, isPending: assignTaskToSessionIsPending } = useAssignTaskToSessionMutation(queryClient, queryKeySingleEmployeeSession, () => setshowDetailEvent(undefined))
    const { mutate: unassignTasks, isPending: unassignTaskToSessionIsPending } = useUnAssignTaskToSessionMutation(queryClient, queryKeySingleEmployeeSession, handleAssignTasks)
    const handleAssignOrUnassignTasks = (id: string) => {
        // Assign Tasks after adding a session
        if (typeof showDetailEvent === "string") {
            assignTasks({
                id: id,
                body: assignedTasks.map((item) => item._id),
                dontDisplaySuccessMessage: true,
            })
        } else {
            // check if we need to assign and unassign tasks
            let areBothAssigningsExecuted =
                !unassignedTasks?.every((item) => tasksByObjectData.includes(item)) &&
                typeof showDetailEvent !== "string" &&
                showDetailEvent?.tasks !== undefined &&
                !assignedTasks.every((item) => showDetailEvent.tasks.includes(item))
            // only assign tasks
            if (!areBothAssigningsExecuted) {
                handleAssignTasks(id)
            }
            handleUnassignTasks(id, areBothAssigningsExecuted)
        }
    }

    // ADD session
    const { mutate: addNewSession, isPending: addNewSessionIsPending } = useAddSessionMutation(queryClient, queryKeySingleEmployeeSession, handleAssignOrUnassignTasks)
    const handleAddNewSession = async () => {
        if (updateSessionCopy) {
            if (assignedTasks.length !== 0) {
                addNewSession(rearrangeSessionDataForDB(updateSessionCopy, tasksByObjectData?.find((task: { _id: string }) => task._id === assignedTasks[0]._id).object))
            }
        }
    }

    // DELETE session
    const { mutate: deleteSession } = useDeleteSessionMutation(queryClient, queryKeySingleEmployeeSession)
    const handleDeleteSession = () => {
        if (showDetailEvent && showDetailEvent !== "new") {
            deleteSession(showDetailEvent.id)
            setshowDetailEvent(undefined)
        }
    }

    // handle Click on event
    const handleClickEvent = (e: any) => {
        if (e.event.calendarId !== "cal2" && e.event.calendarId !== "cal3") {
            if (ability.can(P.termine_management.action, P.termine_management.subject)) {
                setshowDetailEvent(allFetchedEvents.find((event) => event.id === e.event.id) as sessionCalendarDataType | "new")
            } else if (ability.can(P.termine_execute.action, P.termine_execute.subject)) {
                navigate(Navigation.SESSION + e.event.id)
            }
        }
    }

    // Get All Employees for search
    const [searchTermEmployee, setsearchTermEmployee] = useState<string>()
    const searchParamsEmployee = useDelayWhenSearchWithPagination(searchTermEmployee ?? "")
    const queryKeyAllEmployees = GeneralQueryKeys.EMPLOYEE_NAMES_ONLY + searchParamsEmployee
    const {
        error: allEmployeeError,
        isLoading: allEmployeeIsLoading,
        data: allEmployeeData,
    } = useQuery({
        queryKey: [queryKeyAllEmployees],
        queryFn: () => getEmployeeNamesOnly(searchParamsEmployee),
    })
    const displayedAllEmployees = useMemo(() => allEmployeeData, [allEmployeeData])

    // Get All Objects for search
    const [searchTermObject, setsearchTermObject] = useState("")
    const searchParamsObject = useDelayWhenSearchWithPagination(searchTermObject)
    // the backend filters by a single cost center only, so with several cost centers no filter is sent
    // (kostenstelle is populated by the backend, ids are accepted as well)
    const employeeCostCenter = singleEmployeeData?.kostenstelle?.length === 1 ? singleEmployeeData.kostenstelle[0] : undefined
    const objectCostCenterId: string | undefined = typeof employeeCostCenter === "string" ? employeeCostCenter : employeeCostCenter?._id
    const queryKeyAllObjects = GeneralQueryKeys.OBJECTS_ALL + searchParamsObject + objectCostCenterId
    const {
        error: allObjectError,
        isLoading: allObjectIsLoading,
        data: allObjectData,
    } = useQuery({
        queryKey: [queryKeyAllObjects],
        queryFn: () => getObjectAddressOnly(searchParamsObject, objectCostCenterId),
        enabled: !!showDetailEvent && !!singleEmployeeData?.kostenstelle,
    })
    const displayedAllObjects = useMemo(() => allObjectData, [allObjectData])

    // GET tasks by object
    const [queryKeyTasksByFilter, setqueryKeyTasksByFilter] = useState<{ obj_id: string; queryKey: string } | undefined>()
    useEffect(() => {
        if (typeof showDetailEvent !== "string" && showDetailEvent?.object_id) {
            setqueryKeyTasksByFilter({ obj_id: showDetailEvent.object_id, queryKey: "tasksByObjKey" + showDetailEvent.object_id })
        }

        if (updateSessionCopy?.object_id) {
            setqueryKeyTasksByFilter({ obj_id: updateSessionCopy.object_id, queryKey: "tasksByObjKey" + updateSessionCopy.object_id })
        }
        if (updateSessionCopy?.object_id === undefined && showDetailEvent === undefined) {
            setqueryKeyTasksByFilter(undefined)
        }
    }, [showDetailEvent, updateSessionCopy?.object_id])

    // GET tasks by object
    const {
        data: tasksByObjectData,
        isLoading: tasksByObjectIsLoading,
        // error: tasksByObjectError,
    } = useQuery({
        queryKey: [queryKeyTasksByFilter?.queryKey],
        queryFn: () =>
            getTasksByFilter({
                unassigned: true,
                billable: undefined,
                objectId: queryKeyTasksByFilter?.obj_id,
                costCenterId: undefined,
            }),
        enabled: !!queryKeyTasksByFilter?.queryKey,
    })
    const [unassignedTasks, setunassignedTasks] = useState<getSingleTaskType[]>()
    useEffect(() => setunassignedTasks(tasksByObjectData), [tasksByObjectData])

    // UPDATE manual checkin or checkout
    const { mutate: checkInOrOutManual } = useCheckInOrOutToSessionMutation(queryClient, queryKeySingleEmployeeSession)
    const handleManualCheckInOrOut = (session?: sessionCalendarDataType) => {
        if (session) {
            checkInOrOutManual({ id: session.id, indicator: session.checkInStatus === "checked-in" ? "out" : "in" })
            setshowDetailEvent(undefined)
        }
    }

    // check for route optimization result in session storage
    const [optimizedResult, setoptimizedResult] = useState<OptimizeRouteResultType | null>()
    useEffect(() => {
        if (localStorage.getItem("resultOfOptimization")) {
            setoptimizedResult(loadWithExpiryFromLocalStorage("resultOfOptimization"))
        }
    }, [])

    // FRONTEND LOGIC
    useEffect(() => {
        if (singleEmployeeData) {
            setNameOfCurrentEmployee(singleEmployeeData.first_name + " " + singleEmployeeData.last_name)
        }
    }, [singleEmployeeData])

    const [form] = useForm()
    useEffect(() => {
        form.setFieldsValue({
            selectObject: (showDetailEvent !== "new" && showDetailEvent ? showDetailEvent : undefined)?.location,
            date: (showDetailEvent !== "new" && showDetailEvent ? showDetailEvent : undefined) ? dayjs((showDetailEvent as sessionCalendarDataType)?.start).format("YYYY-MM-DD") : "",
            start: (showDetailEvent !== "new" && showDetailEvent ? showDetailEvent : undefined) ? dayjs((showDetailEvent as sessionCalendarDataType)?.start).format("HH:mm") : "",
            end: (showDetailEvent !== "new" && showDetailEvent ? showDetailEvent : undefined) ? dayjs((showDetailEvent as sessionCalendarDataType)?.end).format("HH:mm") : "",
            selectedTasks: (showDetailEvent !== "new" && showDetailEvent ? showDetailEvent : undefined)?.tasks,
        })
    }, [form, showDetailEvent])

    return (
        <>
            <BasePage nopadding="p-0 sm:p-4">
                <BaseHeadBanner
                    title={"Kalender & Termine"}
                    button={
                        <Authorized allOf={[P.termine_management]}>
                            <PLUSCIRCLE
                                className="w-8 h-8 cursor-pointer"
                                onClick={() => {
                                    setunassignedTasks(undefined)
                                    setqueryKeyTasksByFilter(undefined)
                                    setassginedTasks([])
                                    setshowDetailEvent("new")
                                    setupdateSessionCopy({
                                        attendees: [singleEmployeeData as { first_name: string; last_name: string; _id: string }],
                                    } as sessionCalendarDataType)
                                }}
                            />
                        </Authorized>
                    }
                    classProps="md:flex hidden"
                />
                <BaseButtonRow>
                    <div className="flex gap-2 items-center sm:pl-4 md:pl-0">
                        {id && !allEmployeeError && (
                            <Authorized allOf={[P.termine_management]}>
                                <BaseInputSelection
                                    label={""}
                                    placeholder={"Auswählen"}
                                    value={
                                        !searchTermEmployee || searchTermEmployee === ""
                                            ? nameOfCurrentEmployee
                                            : singleEmployeeData?.first_name !== undefined
                                            ? singleEmployeeData?.first_name + " " + singleEmployeeData?.last_name
                                            : "Lädt..."
                                    }
                                    classProps="!w-80 pt-2 pb-3 hidden sm:flex "
                                    onSearch={setsearchTermEmployee}
                                    customLoading={allEmployeeIsLoading}
                                    data={displayedAllEmployees}
                                    setId={(e: string) => {
                                        setNameOfCurrentEmployee(e)
                                        setshowDetailEvent(undefined)
                                        navigate("/kalender/" + e)
                                    }}
                                />
                                <BaseButton
                                    hideLabelOnMobile="md"
                                    classProps="btn bg-white md:hidden ml-8"
                                    label={""}
                                    icon={<PLUSCIRCLE className="w-6 h-6" />}
                                    func={() => {
                                        setunassignedTasks(undefined)
                                        setqueryKeyTasksByFilter(undefined)
                                        setassginedTasks([])
                                        setshowDetailEvent("new")
                                        setupdateSessionCopy({
                                            attendees: [singleEmployeeData as { first_name: string; last_name: string; _id: string }],
                                        } as sessionCalendarDataType)
                                    }}
                                />
                            </Authorized>
                        )}
                    </div>
                    <div className="flex gap-2 items-center justify-between sm:justify-start w-full sm:w-auto pr-2 sm:pr-0">
                        <Authorized allOf={[P.termine_management]}>
                            {currentCalendarView === "week" && optimizedResult && Object.keys(optimizedResult).length !== 0 && (
                                <BaseButton label="Optimierte Route anzeigen" func={() => modalCon?.trigger(2)} icon={undefined} />
                            )}
                            {currentCalendarView === "week" && (!optimizedResult || Object.keys(optimizedResult).length === 0) && (
                                <ARROWRIGHTLEFT className="w-10 h-10 cursor-pointer shadow-md rounded-default bg-white p-2" onClick={() => modalCon?.trigger(2)} />
                            )}
                        </Authorized>
                        <BaseButton
                            hideLabelOnMobile="sm"
                            classProps="btn bg-white"
                            label={currentCalendarView === "day" ? "Wochenansicht" : "Tagesansicht"}
                            icon={<CALENDAR className="w-6 h-6" />}
                            func={handleChangeCalendarView}
                        />
                        <div className="flex gap-2 items-center">
                            <ARROWLEFT onClick={goToPrevWeek} className="p-2 bg-white shadow-md rounded-default w-10 h-10 cursor-pointer " />
                            <ARROWRIGHT onClick={goToNextWeek} className="p-2 bg-white shadow-md rounded-default w-10 h-10 cursor-pointer " />
                        </div>
                    </div>
                </BaseButtonRow>
                <BaseGrowBody classProps="grow flex relative">
                    {eventError && <ErrorPage />}
                    {eventisLoading && <Loading classProps="absolute top-0 z-[1000000]" />}
                    <Calendar
                        ref={calendarRef}
                        view={currentCalendarView}
                        calendars={calendars}
                        height="100%"
                        events={allFetchedEvents.filter((event) => event !== undefined)}
                        useDetailPopup={false}
                        onClickEvent={(event: any) => {
                            handleClickEvent(event)
                        }}
                        onBeforeUpdateEvent={handleBeforeUpdateSchedule}
                        template={{
                            allday(event: any) {
                                return `<span className="overflow-hidden">${event.title}</span>`
                            },
                            time: function (schedule: any) {
                                const start = dayjs(schedule.start).format("HH:mm")
                                const end = dayjs(schedule.end).format("HH:mm")

                                return `
                                    <div className="hidden sm:block">${start} Uhr - ${end} Uhr</div>
                                    <div>${schedule.title}</div>
                                `
                            },
                            timegridDisplayPrimaryTime: (hour: any) => {
                                const hours = hour.time.getHours()
                                if (window.innerWidth > 768) {
                                    return `${hours}:00 Uhr`
                                } else {
                                    return `${hours}`
                                }
                            },
                        }}
                        isReadOnly={!ability.can(P.termine_management.action, P.termine_management.subject)}
                        week={{
                            hourStart: 0,
                            hourEnd: 24,
                            workweek: true,
                            dayNames: window.innerWidth > 768 ? ["Sonntag", `Montag`, "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"] : ["", "", "", "", "", "", ""],
                            timezonesCollapsed: true,
                            taskView: false, // Hide the "Tasks" section
                            eventView: ["time", "allday"], // Show only time events, hiding "All-day events"
                        }}
                    />
                    <SessionDetailSidebar
                        addAttendee={() => {
                            modalCon?.trigger(1)
                            setsearchTermEmployee("")
                        }}
                        setsideBarLoading={addNewSessionIsPending || assignTaskToSessionIsPending || unassignTaskToSessionIsPending}
                        objectIsLoading={allObjectIsLoading}
                        searchForObject={(e) => setsearchTermObject(e)}
                        objects={displayedAllObjects}
                        close={() => setshowDetailEvent(undefined)}
                        session={showDetailEvent !== "new" && showDetailEvent ? showDetailEvent : undefined}
                        form={form}
                        showDetailEvent={showDetailEvent ? true : false}
                        setUpdatedData={setupdateSessionCopy}
                        updatedData={updateSessionCopy}
                        handleAddNewSession={handleAddNewSession}
                        handleUpdateSession={handleUpdateSession}
                        handleDeleteSession={handleDeleteSession}
                        assginedTasks={assignedTasks ?? []}
                        setassginedTasks={setassginedTasks}
                        unassignedTasks={unassignedTasks}
                        setunassignedTasks={setunassignedTasks}
                        tasksByObjectIsLoading={tasksByObjectIsLoading}
                        manualCheckInOrOut={handleManualCheckInOrOut}
                    />
                </BaseGrowBody>
                {allEmployeeData && (
                    <EditAttendeesOfSessionModal
                        modalId={1}
                        selectedEmployeeCalendar={id}
                        sessionCompleted={(showDetailEvent as sessionCalendarDataType)?.status === "completed"}
                        setEmployee={showDetailEvent !== "new" ? setshowDetailEvent : setupdateSessionCopy}
                        employees={displayedAllEmployees}
                        onSave={showDetailEvent !== "new" ? () => handleUpdateAttendees() : () => modalCon?.trigger(0)}
                        currentAttendees={showDetailEvent !== "new" ? showDetailEvent?.attendees ?? [] : updateSessionCopy?.attendees ?? []}
                    />
                )}
                <RouteOptimizationModal
                    modalId={2}
                    employee={singleEmployeeData}
                    startDate={startInterval as Dayjs}
                    queryKey={queryKeySingleEmployeeSession}
                    setOptimizedResult={setoptimizedResult}
                    closeModal={() => modalCon?.trigger(2)}
                />
                <ModalCheckAction
                    modalId={3}
                    headline="Optimierten Termin aktualisieren?"
                    text="Sie sind dabei, einen optimierten Termin zu aktualisieren. Diese Aktion kann nicht rückgängig gemacht werden und könnte Konflikte mit anderen Terminen verursachen."
                    buttonText="Trotzdem aktualisieren"
                    buttonColor="btn-primary"
                    func={() => {
                        updateSessionTimeOnly(eventUpdate)
                    }}
                />
            </BasePage>
        </>
    )
}
